import React from 'react';
import { Typography } from 'components/atoms';
import './PerformanceTooltip.scss';

interface PerformanceData {
  totalGoals: number;
  completedGoals: number;
  pendingGoals: number;
  inProgressGoals: number;
  acceptedGoals?: number;
  declinedGoals?: number;
  totalSubtasks: number;
  completedSubtasks: number;
  pendingSubtasks: number;
  inProgressSubtasks: number;
  goalsCompletionRate: number;
  subtasksCompletionRate: number;
  overallPerformanceRate: number;
}

interface PerformanceTooltipProps {
  performance: PerformanceData;
  isVisible: boolean;
  position: { x: number; y: number };
  showBelow?: boolean;
  isPinned?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const PerformanceTooltip: React.FC<PerformanceTooltipProps> = ({
  performance,
  isVisible,
  position,
  showBelow = false,
  isPinned = false,
  onMouseEnter,
  onMouseLeave
}) => {
  if (!isVisible) return null;

  return (
    <div 
      className={`performance-tooltip ${showBelow ? 'show-below' : 'show-above'} ${isPinned ? 'pinned' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        '--tooltip-x': `${position.x}px`,
      } as React.CSSProperties}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={(e) => e.stopPropagation()} // Prevent background click from closing tooltip
    >
      <div className="tooltip-header">
        <Typography 
          type="small_regular" 
          text={isPinned ? "Performance Details (Pinned)" : "Performance Details"} 
          weight="bold" 
          color="white" 
        />
      </div>
      
      <div className="tooltip-content">
        <div className="performance-section">
          <Typography type="xs_regular" text="Goals Overview" weight="bold" color="gray" />
          <div className="performance-grid">
            <div className="performance-item">
              <Typography type="xs_regular" text="Total Goals" color="gray" />
              <Typography type="small_regular" text={performance.totalGoals.toString()} weight="bold" />
            </div>
            <div className="performance-item">
              <Typography type="xs_regular" text="Completed" color="gray" />
              <Typography type="small_regular" text={performance.completedGoals.toString()} weight="bold"  />
            </div>
            <div className="performance-item">
              <Typography type="xs_regular" text="In Progress" color="gray" />
              <Typography type="small_regular" text={performance.inProgressGoals.toString()} weight="bold" />
            </div>
            <div className="performance-item">
              <Typography type="xs_regular" text="Pending" color="gray" />
              <Typography type="small_regular" text={performance.pendingGoals.toString()} weight="bold" />
            </div>
          </div>
        </div>

        <div className="performance-section">
          <Typography type="xs_regular" text="Subtasks Overview" weight="bold" color="gray" />
          <div className="performance-grid">
            <div className="performance-item">
              <Typography type="xs_regular" text="Total Subtasks" color="gray" />
              <Typography type="small_regular" text={performance.totalSubtasks.toString()} weight="bold" />
            </div>
            <div className="performance-item">
              <Typography type="xs_regular" text="Completed" color="gray" />
              <Typography type="small_regular" text={performance.completedSubtasks.toString()} weight="bold" />
            </div>
            <div className="performance-item">
              <Typography type="xs_regular" text="In Progress" color="gray" />
              <Typography type="small_regular" text={performance.inProgressSubtasks.toString()} weight="bold" />
            </div>
            <div className="performance-item">
              <Typography type="xs_regular" text="Pending" color="gray" />
              <Typography type="small_regular" text={performance.pendingSubtasks.toString()} weight="bold" />
            </div>
          </div>
        </div>

        {(performance.acceptedGoals !== undefined || performance.declinedGoals !== undefined) && (
          <div className="performance-section">
            <Typography type="xs_regular" text="Acceptance Status" weight="bold" color="gray" />
            <div className="performance-grid">
              <div className="performance-item">
                <Typography type="xs_regular" text="Accepted Goals" color="gray" />
                <Typography type="small_regular" text={(performance.acceptedGoals || 0).toString()} weight="bold" color="success" />
              </div>
              <div className="performance-item">
                <Typography type="xs_regular" text="Declined Goals" color="gray" />
                <Typography type="small_regular" text={(performance.declinedGoals || 0).toString()} weight="bold" color="danger" />
              </div>
            </div>
          </div>
        )}

        <div className="performance-section">
          <Typography type="xs_regular" text="Completion Rates" weight="bold" color="gray" />
          <div className="completion-rates">
            <div className="rate-item">
              <Typography type="xs_regular" text="Goals Completion" color="gray" />
              <div className="rate-bar">
                <div 
                  className="rate-fill"
                  style={{ width: `${performance.goalsCompletionRate}%` }}
                />
              </div>
              <Typography type="xs_regular" text={`${performance.goalsCompletionRate}%`} weight="bold" />
            </div>
            <div className="rate-item">
              <Typography type="xs_regular" text="Subtasks Completion" color="gray" />
              <div className="rate-bar">
                <div 
                  className="rate-fill"
                  style={{ width: `${performance.subtasksCompletionRate}%` }}
                />
              </div>
              <Typography type="xs_regular" text={`${performance.subtasksCompletionRate}%`} weight="bold" />
            </div>
            <div className="rate-item overall-rate">
              <Typography type="xs_regular" text="Overall Performance" color="gray" />
              <div className="rate-bar">
                <div 
                  className="rate-fill overall"
                  style={{ width: `${performance.overallPerformanceRate}%` }}
                />
              </div>
              <Typography type="small_regular" text={`${performance.overallPerformanceRate}%`} weight="bold" color="primary" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 